<script>
import * as api from '../../../api/elixioConnectApi'
import EcButton from '../../ui/EcButton.vue'
import EcAlert from '../../ui/EcAlert.vue'
import EcBadge from '../../ui/EcBadge.vue'
import EcSpinner from '../../ui/EcSpinner.vue'
import EcTabs from '../../ui/EcTabs.vue'
import EcTabPane from '../../ui/EcTabPane.vue'
import GenericServiceConfig from './GenericServiceConfig.vue'

export default {
  name: 'GHLConfig',
  components: { EcButton, EcAlert, EcBadge, EcSpinner, EcTabs, EcTabPane, GenericServiceConfig },
  props: {
    service: { type: Object, required: true },
    organization: { type: Object, required: true },
  },
  data() {
    return {
      activeTab: 'config',
      importing: false,
      importResult: null,
      importError: '',
      pollTimer: null,
      resultRows: [
        { key: 'created', variant: 'success' },
        { key: 'linked', variant: 'info' },
        { key: 'updated', variant: 'primary' },
        { key: 'skipped', variant: 'secondary' },
        { key: 'errors', variant: 'danger' },
      ],
    }
  },
  computed: {
    t() {
      return this.$ecTranslate || ((k) => k)
    },
    notify() {
      return this.$ecNotify || (() => {})
    },
    confirm() {
      return this.$ecConfirm || ((msg) => Promise.resolve(window.confirm(msg)))
    },
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    async startImport() {
      const confirmed = await this.confirm(this.t('ELIXIO_CONNECT.GHL_IMPORT_CONFIRM'))
      if (!confirmed) return

      this.importing = true
      this.importError = ''
      this.importResult = null

      try {
        const { data } = await api.importGHLContacts(this.organization.id)

        if (data.status === 'already_running') {
          this.importError = this.t('ELIXIO_CONNECT.GHL_IMPORT_ALREADY_RUNNING')
          this.importing = false
          return
        }

        this.startPolling()
      } catch (error) {
        console.error('GHL import =>', error)
        this.importError = this.t('ELIXIO_CONNECT.GHL_IMPORT_FAILED')
        this.importing = false
      }
    },
    startPolling() {
      this.pollTimer = setInterval(async () => {
        try {
          const { data } = await api.getGHLImportStatus(this.organization.id)

          if (data.status === 'completed') {
            this.importResult = data
            this.importing = false
            this.stopPolling()
            this.notify(this.t('ELIXIO_CONNECT.GHL_IMPORT_COMPLETED'), 'success')
          } else if (data.status === 'failed') {
            this.importError = this.t('ELIXIO_CONNECT.GHL_IMPORT_FAILED')
            this.importing = false
            this.stopPolling()
          }
        } catch {
          this.stopPolling()
          this.importing = false
        }
      }, 5000)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
  },
}
</script>

<template>
  <GenericServiceConfig
    :service="service"
    :organization="organization"
    @submitted="$emit('submitted', $event)"
    @cancelled="$emit('cancelled')"
  >
    <!-- Inject GHL-specific tab into the generic config -->
    <template #extra-tabs="{ serviceData }">
      <EcTabPane
        v-if="serviceData.is_active"
        :label="t('ELIXIO_CONNECT.GHL_IMPORT_CONTACTS_TAB')"
        name="tab-ghl-import"
      >
        <div class="ec-ghl-import">
          <h5 class="ec-mb-3">{{ t('ELIXIO_CONNECT.GHL_IMPORT_TITLE') }}</h5>

          <!-- Error -->
          <EcAlert v-if="importError" type="danger" :dismissible="true" class="ec-mb-3">
            {{ importError }}
          </EcAlert>

          <!-- Import in progress -->
          <div v-if="importing" class="ec-text-center" style="padding: 2rem 0">
            <EcSpinner class="ec-mb-3" />
            <p class="ec-text-muted">{{ t('ELIXIO_CONNECT.GHL_IMPORT_IN_PROGRESS') }}</p>
            <small class="ec-text-muted">
              {{ t('ELIXIO_CONNECT.GHL_IMPORT_BACKGROUND_NOTE') }}
            </small>
          </div>

          <!-- Import results -->
          <div v-else-if="importResult" class="ec-mb-3">
            <EcAlert type="success" class="ec-mb-3">
              {{ t('ELIXIO_CONNECT.GHL_IMPORT_COMPLETED') }}
            </EcAlert>

            <div class="ec-import-results">
              <div
                v-for="row in resultRows"
                :key="row.key"
                class="ec-import-results__row"
              >
                <template v-if="importResult[row.key] != null">
                  <span>{{ t(`ELIXIO_CONNECT.GHL_IMPORT_${row.key.toUpperCase()}`) }}</span>
                  <EcBadge :variant="row.variant">{{ importResult[row.key] }}</EcBadge>
                </template>
              </div>
            </div>
          </div>

          <!-- Start button -->
          <EcButton
            v-if="!importing"
            variant="primary"
            icon="download"
            @click="startImport"
          >
            {{ t('ELIXIO_CONNECT.GHL_IMPORT_START') }}
          </EcButton>
        </div>
      </EcTabPane>
    </template>
  </GenericServiceConfig>
</template>

<style scoped>
.ec-import-results__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--ec-border-color);
  font-size: var(--ec-font-size-sm);
}

.ec-import-results__row:last-child {
  border-bottom: none;
}
</style>
